/**
 * @file src/main.jsx
 * @description 
 * Authoritative Platform Entry Point.
 * Orchestrates top-level providers, error boundaries, and global visual overlays.
 * 
 * Standards:
 * - 4.1: Deterministic Booting.
 * - 6.0: Zero Dead Code (Purged shared/ folder).
 * - 1.5: Object-Oriented Functional Boundaries.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';

// Context Providers
import { ToastProvider, useToast } from './context/ToastContext.jsx';
import { ConfirmHost } from './components/elements/confirmDialog.jsx';
import { NotificationsProvider } from './context/NotificationsContext.jsx';
import { SocketProvider } from './context/SocketContext.jsx';
import { MaintenanceProvider, MaintenanceLayer } from './context/MaintenanceContext.jsx';

// 🏛️ Atomic Snippets
import { ToastOverlay } from './components/snippets/toast-overlay.snippet.jsx';

// Global styles
import './styles/index.css';

import { storage } from './utils/storage.js';

// 🧹 Before anything renders: drop identity this browser is holding from a
// build that kept it in localStorage. An abandoned onboarding used to leave
// the previous person's email address readable by the next visitor, including
// an anonymous one (walked 2026-09-08). Cheap, synchronous, and safe when
// storage is blocked.
storage.purgeStaleIdentity();

/**
 * 🛰️ TOAST ROOT
 * Logic: Reactive shell that injects the ToastOverlay into the global DOM.
 * Standard 1.5: Separates state management from visual rendering.
 */
function ToastRoot({ children }) {
  const { toasts } = useToast();
  return (
    <>
      {children}
      {/* Refactored from legacy ToastContainer */}
      <ToastOverlay toasts={toasts} />
    </>
  );
}

/**
 * 🚀 MOUNT ORCHESTRATOR
 */
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ToastProvider>
        <SocketProvider>
          <NotificationsProvider>
            <ToastRoot>
              <MaintenanceProvider>
                <MaintenanceLayer>
                  <App />
                </MaintenanceLayer>
              </MaintenanceProvider>
              {/* The one host for confirmDialog() — see components/elements/confirmDialog.jsx */}
              <ConfirmHost />
            </ToastRoot>
          </NotificationsProvider>
        </SocketProvider>
      </ToastProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
